import React from 'react';
import FieldLevelHelp from '../components/FieldLevelHelp';

/**
 * Component to display and control a Credit Card payment.
 *
 * @param paymentDetails Details to fill the form
 * @param handlePaymentInputChange A function to call when form details change
 * @param onSubmit The function to call when the form is submitted (Used for submit-on-enter)
 * @param isInternational Whether the payment is coming from an international source (Not United States)
 */
function CreditCardForm({ paymentDetails, handlePaymentInputChange, onSubmit, isInternational }) {
	const zipCodeLabel = isInternational ? 'Billing Postal Code' : 'Billing Zip Code';
	return (
		<div>
			<p className="text-default">
				<strong>Please enter credit card information.</strong>
				<br />
				<em>
					<small>We accept American Express, MasterCard, Visa, or Discover cards.</small>
				</em>
			</p>
			<form onSubmit={onSubmit}>
				{/* 'submit' button is required to make submit on enter work */}
				<input type="submit" style={{ display: 'none' }} />
				<div className="row">
					<div
						className={'form-group col-md-6 ' + (paymentDetails.cardNo.valid === false ? 'has-error' : '')}
					>
						<label className="col-md-4 control-label" htmlFor="cardNo">
							Card No<span className="text-danger">*</span>
						</label>
						<div className="col-md-8">
							<input
								type="text"
								id="cardNo"
								name="cardNo"
								placeholder="Enter Card Number"
								maxLength="19"
								value={paymentDetails.cardNo.value}
								onChange={handlePaymentInputChange}
							/>
							{paymentDetails.cardNo.valid === false && (
								<div className="validation-message text-danger">{paymentDetails.cardNo.message}</div>
							)}
						</div>
					</div>
					<div
						className={
							'form-group col-md-6 ' + (paymentDetails.cardExpDate.valid === false ? 'has-error' : '')
						}
					>
						<label className="col-md-4 control-label" htmlFor="cardExpDate">
							Exp. Date
							<FieldLevelHelp
								helpText='Format: two digit month / two digit year; e.g. "12/23"'
								linkText=""
							/>
							<span className="text-danger">*</span>
						</label>
						<div className="col-md-8">
							<input
								type="text"
								id="cardExpDate"
								name="cardExpDate"
								placeholder="MM/YY"
								value={paymentDetails.cardExpDate.value}
								onChange={handlePaymentInputChange}
							/>
							{paymentDetails.cardExpDate.valid === false && (
								<div className="validation-message text-danger">
									{paymentDetails.cardExpDate.message}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="row">
					<div
						className={'form-group col-md-6 ' + (paymentDetails.cardCvv.valid === false ? 'has-error' : '')}
					>
						<label className="col-md-4 control-label" htmlFor="cvv">
							CVV Code<span className="text-danger">*</span>
						</label>
						<div className="col-md-8">
							<input
								type="text"
								id="cardCvv"
								name="cardCvv"
								maxLength="4"
								placeholder="Enter Security Code"
								value={paymentDetails.cardCvv.value}
								onChange={handlePaymentInputChange}
							/>
							{paymentDetails.cardCvv.valid === false && (
								<div className="validation-message text-danger">{paymentDetails.cardCvv.message}</div>
							)}
						</div>
					</div>
					<div
						className={
							'form-group col-md-6 ' + (paymentDetails.cardholderName.valid === false ? 'has-error' : '')
						}
					>
						<label className="col-md-4 control-label" htmlFor="cardholderName">
							Cardholder Name<span className="text-danger">*</span>
						</label>
						<div className="col-md-8">
							<input
								type="text"
								id="cardholderName"
								name="cardholderName"
								placeholder="As Displayed on Card"
								value={paymentDetails.cardholderName.value}
								onChange={handlePaymentInputChange}
							/>
							{paymentDetails.cardholderName.valid === false && (
								<div className="validation-message text-danger">
									{paymentDetails.cardholderName.message}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="row">
					<div
						className={
							'form-group col-md-6 ' + (paymentDetails.cardZipCode.valid === false ? 'has-error' : '')
						}
					>
						<label className="col-md-4 control-label" htmlFor="billingZipCode">
							{zipCodeLabel}
							<span className="text-danger">*</span>
						</label>
						<div className="col-md-8">
							<input
								type="text"
								maxLength="5"
								id="cardZipCode"
								name="cardZipCode"
								placeholder={`Enter ${zipCodeLabel}`}
								value={paymentDetails.cardZipCode.value}
								onChange={handlePaymentInputChange}
							/>
							{paymentDetails.cardZipCode.valid === false && (
								<div className="validation-message text-danger">
									{isInternational
										? 'Please provide a billing postal code'
										: paymentDetails.cardZipCode.message}
								</div>
							)}
						</div>
					</div>
				</div>
			</form>
		</div>
	);
}

/**
 * Validate each form field and return
 *
 * @param paymentDetails The form data
 * @return The updated form
 */
const validateCreditCardForm = (paymentDetails = {}) => {
	if (paymentDetails.tokenId?.value) {
		return {
			...paymentDetails,
			tokenId: {
				...paymentDetails.tokenId,
				valid: true
			}
		}
	}

	let validatedForm = {
		cardNo: validateCreditCardField('cardNo', paymentDetails.cardNo),
		cardCvv: validateCreditCardField('cardCvv', paymentDetails.cardCvv),
		cardExpDate: validateCreditCardField('cardExpDate', paymentDetails.cardExpDate),
		cardholderName: validateCreditCardField('cardholderName', paymentDetails.cardholderName),
		cardZipCode: validateCreditCardField('cardZipCode', paymentDetails.cardZipCode),
		saveForLater: validateCreditCardField('saveForLater', paymentDetails.saveForLater)
	};

	return validatedForm;
};

/**
 * Validate a single form field
 *
 * @param key The key of the field to validate
 * @param detail The form details of the field to validate
 * @return The validated field
 */
const validateCreditCardField = (key, detail) => {
	let valid = true;
	let value = (detail || {}).value;
	let message = '';
	switch (key) {
		case 'cardNo':
			if (!value) {
				valid = false;
				message = 'Please provide a card number.';
			} else if (value.length < 12 || value.length > 19) {
				valid = false;
				message = 'Card number must be between 12 and 19 digits.';
			}
			break;
		case 'cardExpDate':
			if (!value) {
				valid = false;
				message = 'Please provide an expiration date.';
			} else if (!new RegExp('(0[1-9]|1[0-2])/(1[8-9]|[2-9][0-9])').test(value)) {
				valid = false;
				message = 'Please provide a valid expiration date';
			}
			break;
		case 'cardCvv':
			if (!value) {
				valid = false;
				message = 'Please provide a CVV code.';
			}
			break;
		case 'cardholderName':
			if (!value) {
				valid = false;
				message = 'Please provide a cardholder name.';
			}
			break;
		case 'cardZipCode':
			if (!value) {
				valid = false;
				message = 'Please provide a billing zip code';
			}
			break;
		default:
			// Do not validate unknown fields
	}

	return { value, valid, message };
};

export default CreditCardForm;

/* Export the valication methods so they can be used outside of this component */
export { validateCreditCardForm, validateCreditCardField };
